import React, { useEffect, useRef, useState } from 'react';

export const EVM_SLICER_LAYOUT_KEY = 'evm-slicer-layout-v2';

const ROW_H = 176;

export const DEFAULT_SLICER_LAYOUT = {
  activities: { order: 0, w: 580, h: ROW_H, search: true, scroll: true },
  discipline: { order: 1, w: 168, h: ROW_H, search: true, scroll: true },
  resources: { order: 2, w: 220, h: ROW_H, search: true, scroll: true, kind: 'dropdown' },
  rsrcname: { order: 2.1, w: 220, h: ROW_H, search: true, scroll: true, kind: 'dropdown' },
  shopfield: { order: 3, w: 140, h: ROW_H, search: false, scroll: false },
  legendby: { order: 5, w: 156, h: ROW_H, search: false, scroll: false },
  family: { order: 3, w: 156, h: ROW_H, search: true, scroll: true },
  groupby: { order: 4, w: 136, h: ROW_H, search: false, scroll: false },
  scope: { order: 5, w: 148, h: ROW_H, search: false, scroll: false },
  fab: { order: 6, w: 150, h: ROW_H, search: false, scroll: false },
  phase: { order: 7, w: 148, h: ROW_H, search: false, scroll: false },
  earnedsrc: { order: 8, w: 220, h: ROW_H, search: false, scroll: true },
  facformula: { order: 8.2, w: 220, h: ROW_H, search: false, scroll: true },
  assumelastweek: { order: 8.4, w: 188, h: ROW_H, search: false, scroll: false },
  safety: { order: 8.6, w: 240, h: ROW_H, search: false, scroll: false },
  supervision: { order: 8.62, w: 196, h: ROW_H, search: false, scroll: false },
  staffnums: { order: 8.7, w: 200, h: ROW_H, search: false, scroll: false },
  staffplan: { order: 8.5, w: 232, h: ROW_H, search: false, scroll: true },
  calendar: { order: 9, w: 220, h: ROW_H, search: false, scroll: false },
  whatif: { order: 10, w: 188, h: ROW_H, search: false, scroll: false },
  spentfeed: { order: 11, w: 168, h: ROW_H, search: false, scroll: false },
  employees: { order: 11, w: 160, h: ROW_H, search: true, scroll: true, kind: 'list' },
  trades: { order: 12, w: 160, h: ROW_H, search: true, scroll: true, kind: 'list' },
  divisions: { order: 13, w: 160, h: ROW_H, search: true, scroll: true, kind: 'list' },
  op_codes: { order: 14, w: 160, h: ROW_H, search: true, scroll: true, kind: 'list' },
  empl_code: { order: 15, w: 150, h: ROW_H, search: true, scroll: true, kind: 'list' },
  op_desc: { order: 16, w: 180, h: ROW_H, search: true, scroll: true, kind: 'list' },
  discipline_code: { order: 17, w: 150, h: ROW_H, search: true, scroll: true, kind: 'list' },
  timesheet: { order: 18, w: 150, h: ROW_H, search: true, scroll: true, kind: 'list' },
  contract: { order: 19, w: 150, h: ROW_H, search: true, scroll: true, kind: 'list' },
  shift: { order: 20, w: 120, h: ROW_H, search: false, scroll: false, kind: 'list' },
  status: { order: 21, w: 130, h: ROW_H, search: false, scroll: false, kind: 'list' },
  entered_by: { order: 22, w: 160, h: ROW_H, search: true, scroll: true, kind: 'list' },
  access_group: { order: 23, w: 150, h: ROW_H, search: true, scroll: true, kind: 'list' }
};

export const FALLBACK_SLICER = { order: 80, w: 160, h: ROW_H, search: true, scroll: true, kind: 'list' };

const LAYOUT_ALIASES = {
  employees: 'empl_name',
  trades: 'trade',
  divisions: 'division',
  op_codes: 'op_code',
  op_desc: 'op_code_description'
};

function slicerOnly(layout) {
  const next = {};
  for (const [id, def] of Object.entries(DEFAULT_SLICER_LAYOUT)) {
    next[id] = { ...def, ...(layout?.[id] || {}) };
  }
  for (const [id, val] of Object.entries(layout || {})) {
    if (id === '_meta') {
      next._meta = val;
      continue;
    }
    if (!DEFAULT_SLICER_LAYOUT[id] && val && typeof val === 'object') {
      next[id] = { ...FALLBACK_SLICER, ...val };
    }
  }
  for (const [from, to] of Object.entries(LAYOUT_ALIASES)) {
    if (layout?.[from] && !layout?.[to]) {
      next[to] = { ...FALLBACK_SLICER, ...(layout[from] || {}) };
    }
  }
  return next;
}

export function loadSlicerLayout() {
  try {
    const raw = localStorage.getItem(EVM_SLICER_LAYOUT_KEY);
    if (!raw) return { ...DEFAULT_SLICER_LAYOUT };
    const parsed = JSON.parse(raw);
    const next = slicerOnly(parsed);
    if (!parsed._meta?.alignedRowH) {
      const target = Math.max(ROW_H, ...Object.values(next).map((s) => Number(s.h) || 0));
      for (const id of Object.keys(next)) next[id] = { ...next[id], h: target };
      saveSlicerLayout({ ...next, _meta: { alignedRowH: target } });
    }
    return next;
  } catch {
    return { ...DEFAULT_SLICER_LAYOUT };
  }
}

export function saveSlicerLayout(layout) {
  try {
    localStorage.setItem(EVM_SLICER_LAYOUT_KEY, JSON.stringify(layout));
  } catch {
    /* ignore quota */
  }
}

const menuBtn = {
  border: 'none',
  background: 'transparent',
  color: '#64748b',
  fontSize: '0.72rem',
  cursor: 'pointer',
  padding: '0 3px',
  lineHeight: 1
};

export default function EvmSlicerCard({
  id,
  title,
  layout,
  onChange,
  onHide,
  onSelectAll,
  selectAllLabel,
  children
}) {
  const cfg = { ...FALLBACK_SLICER, ...(DEFAULT_SLICER_LAYOUT[id] || {}), ...(layout?.[id] || {}) };
  const [menuOpen, setMenuOpen] = useState(false);
  const [query, setQuery] = useState('');
  const menuRef = useRef(null);
  const dragRef = useRef(null);

  useEffect(() => {
    if (!menuOpen) return undefined;
    const close = (e) => {
      if (!menuRef.current?.contains(e.target)) setMenuOpen(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [menuOpen]);

  const patch = (partial) => {
    onChange((prev) => {
      const next = {
        ...prev,
        [id]: { ...FALLBACK_SLICER, ...(DEFAULT_SLICER_LAYOUT[id] || {}), ...(prev[id] || {}), ...partial }
      };
      saveSlicerLayout(next);
      return next;
    });
  };

  const startResize = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const startX = e.clientX;
    const startY = e.clientY;
    const startW = cfg.w;
    const startH = cfg.h;
    const onMove = (ev) => {
      patch({
        w: Math.max(110, Math.round(startW + ev.clientX - startX)),
        h: Math.max(72, Math.round(startH + ev.clientY - startY))
      });
    };
    const onUp = () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  };

  const onDragStart = (e) => {
    e.dataTransfer.setData('text/evm-slicer', id);
    e.dataTransfer.effectAllowed = 'move';
    dragRef.current = id;
  };

  const onDrop = (e) => {
    e.preventDefault();
    const from = e.dataTransfer.getData('text/evm-slicer');
    if (!from || from === id) return;
    onChange((prev) => {
      const a = prev[from]?.order ?? 99;
      const b = prev[id]?.order ?? 99;
      const next = {
        ...prev,
        [from]: { ...FALLBACK_SLICER, ...(DEFAULT_SLICER_LAYOUT[from] || {}), ...(prev[from] || {}), order: b },
        [id]: { ...FALLBACK_SLICER, ...(DEFAULT_SLICER_LAYOUT[id] || {}), ...(prev[id] || {}), order: a }
      };
      saveSlicerLayout(next);
      return next;
    });
  };

  const body = typeof children === 'function' ? children(cfg.search ? query : '') : children;

  return (
    <div
      onDragOver={(e) => e.preventDefault()}
      onDrop={onDrop}
      style={{
        position: 'relative',
        order: cfg.order ?? 0,
        width: cfg.w,
        height: cfg.h,
        minWidth: 110,
        minHeight: 72,
        flex: 'none',
        background: 'white',
        border: '1px solid #e2e8f0',
        borderRadius: 6,
        boxSizing: 'border-box',
        padding: '0.28rem 0.35rem 0.45rem',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden'
      }}
    >
      <div
        draggable
        onDragStart={onDragStart}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 4,
          marginBottom: 3,
          paddingBottom: 2,
          borderBottom: '1px solid #e5e7eb',
          cursor: 'grab'
        }}
      >
        <span style={{ color: '#94a3b8', fontSize: 10, userSelect: 'none' }}>⋮⋮</span>
        <span style={{ fontWeight: 700, color: '#334155', fontSize: '0.68rem', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {title}
        </span>
        {onSelectAll && (
          <button type="button" onClick={onSelectAll} style={{ ...menuBtn, fontWeight: 600 }}>
            {selectAllLabel || 'All'}
          </button>
        )}
        <div ref={menuRef} style={{ position: 'relative' }}>
          <button type="button" onClick={() => setMenuOpen((v) => !v)} style={menuBtn} title="Slicer options">⋯</button>
          {menuOpen && (
            <div style={{
              position: 'absolute',
              right: 0,
              top: '100%',
              zIndex: 50,
              width: 168,
              background: 'white',
              border: '1px solid #cbd5e1',
              borderRadius: 6,
              boxShadow: '0 8px 20px rgba(15,23,42,0.14)',
              padding: '0.35rem 0.4rem',
              fontSize: '0.7rem'
            }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '3px 0', cursor: 'pointer' }}>
                <input type="checkbox" checked={!!cfg.search} onChange={() => patch({ search: !cfg.search })} />
                Search
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '3px 0', cursor: 'pointer' }}>
                <input type="checkbox" checked={!!cfg.scroll} onChange={() => patch({ scroll: !cfg.scroll })} />
                Scroll list
              </label>
              <div style={{ marginTop: 4, paddingTop: 4, borderTop: '1px solid #e2e8f0', color: '#64748b', fontSize: '0.65rem' }}>Type</div>
              {[{ id: 'list', label: 'Checkbox list' }, { id: 'dropdown', label: 'Dropdown' }].map((k) => (
                <label key={k.id} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '3px 0', cursor: 'pointer' }}>
                  <input type="radio" name={`slicer-kind-${id}`} checked={(cfg.kind || 'list') === k.id} onChange={() => patch({ kind: k.id })} />
                  {k.label}
                </label>
              ))}
              <button type="button" onClick={() => {
                const def = DEFAULT_SLICER_LAYOUT[id] || FALLBACK_SLICER;
                patch({ w: def.w, h: def.h });
                setMenuOpen(false);
              }}
                style={{ display: 'block', width: '100%', textAlign: 'left', border: 'none', background: 'none', padding: '4px 0', cursor: 'pointer', color: '#334155' }}>
                Reset size
              </button>
              {onHide && (
                <button type="button" onClick={() => { onHide(); setMenuOpen(false); }}
                  style={{ display: 'block', width: '100%', textAlign: 'left', border: 'none', background: 'none', padding: '4px 0', cursor: 'pointer', color: '#b91c1c' }}>
                  Hide slicer
                </button>
              )}
            </div>
          )}
        </div>
      </div>
      {cfg.search && (
        <input
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search…"
          style={{
            width: '100%',
            boxSizing: 'border-box',
            height: 20,
            marginBottom: 3,
            fontSize: '0.65rem',
            border: '1px solid #d1d5db',
            borderRadius: 4,
            padding: '0 0.3rem',
            flex: 'none'
          }}
        />
      )}
      <div style={{ flex: 1, minHeight: 0, overflow: cfg.scroll ? 'auto' : 'visible' }}>
        {body}
      </div>
      <div
        onMouseDown={startResize}
        title="Drag to resize"
        style={{
          position: 'absolute',
          right: 1,
          bottom: 1,
          width: 12,
          height: 12,
          cursor: 'nwse-resize',
          background: 'linear-gradient(135deg, transparent 50%, #94a3b8 50%)',
          borderRadius: 1
        }}
      />
    </div>
  );
}
